import { useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Star, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Avatar } from "@/components/common";
import { useCollection, FormDialog } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const STAGES = ["applied", "screening", "interview", "offered", "hired"];

export default function Recruitment() {
  const { can } = useAuth();
  const { rows, create, update, remove } = useCollection("candidates");
  const [open, setOpen] = useState(false);
  const full = can("people.recruitment", "edit");
  const move = async (c, dir) => { const i = STAGES.indexOf(c.stage); const n = STAGES[i + dir]; if (!n) return; await update(c.id, { stage: n }); toast.success(`${c.name} → ${n}`); };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="People · Hiring" title="Recruitment pipeline" description="Candidates move left to right until they're hired and filed as employees."
        actions={full && <Button className="rounded-full" data-testid="candidates-new-button" onClick={() => setOpen(true)}><Plus size={16} className="mr-1" /> Add candidate</Button>} />
      <div className="flex gap-4 overflow-x-auto pb-2 rise" data-testid="recruitment-board">
        {STAGES.map((s) => {
          const items = rows.filter((c) => c.stage === s);
          return (
            <div key={s} className="kanban-col" data-testid={`kanban-col-${s}`}>
              <div className="flex items-center justify-between px-1 mb-3"><span className="font-serif font-semibold capitalize">{s}</span><span className="font-mono text-[10px] text-muted-foreground bg-card px-2 py-0.5 rounded-full border border-border">{items.length}</span></div>
              <div className="space-y-2">
                {items.map((c) => (
                  <div key={c.id} className="paper-card p-3 group" data-testid={`recruitment-candidate-${c.id}`}>
                    <div className="flex items-center gap-2"><Avatar src={c.avatar} name={c.name} size={32} /><div className="min-w-0"><div className="text-sm font-medium truncate">{c.name}</div><div className="text-[11px] text-muted-foreground truncate">{c.role}</div></div></div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex gap-0.5">{[1, 2, 3, 4, 5].map((n) => <Star key={n} size={10} className={n <= (c.rating || 0) ? "fill-amber text-amber" : "text-border"} />)}</div>
                      <span className="font-mono text-[10px] text-muted-foreground">{c.source}</span>
                    </div>
                    {full && (
                      <div className="mt-2 flex justify-between opacity-60 group-hover:opacity-100 transition-opacity">
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`candidate-prev-${c.id}`} disabled={s === "applied"} onClick={() => move(c, -1)}><ChevronLeft size={14} /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" data-testid={`candidate-delete-${c.id}`} onClick={() => remove(c.id)}><Trash2 size={12} /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`candidate-next-${c.id}`} disabled={s === "hired"} onClick={() => move(c, 1)}><ChevronRight size={14} /></Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
      <FormDialog open={open} onOpenChange={setOpen} title="Add candidate" testId="candidate-form-dialog" initial={{ stage: "applied", rating: 3, source: "LinkedIn" }}
        fields={[{ name: "name", label: "Name", required: true }, { name: "email", label: "Email", type: "email" }, { name: "role", label: "Applying for", required: true }, { name: "source", label: "Source", type: "select", options: ["LinkedIn", "Referral", "Website", "Agency"] }, { name: "stage", label: "Stage", type: "select", options: STAGES }, { name: "rating", label: "Rating (1-5)", type: "number" }]}
        onSubmit={async (f) => { await create({ ...f, avatar: `https://api.dicebear.com/9.x/notionists/svg?seed=${encodeURIComponent(f.name)}` }); toast.success("Candidate added"); }} />
    </div>
  );
}
